// 文件夹弹层(spec 260508-folder-feature §5.4)。
//
// 关键约束:
//   - 不进入 WindowManager / 任务栏。z-index 介于桌面图标(10)与窗口宿主(20)
//     之间,与 .os-folder-popover-* CSS 一致。
//   - 关闭条件:点 backdrop / ESC / 点击 App / 文件夹被解散(items 空)。
//   - 焦点管理:打开时聚焦第一个 App;ESC 关闭后焦点回到触发文件夹按钮(由
//     Desktop 通过 lastFocusedFolderRef 注入)。
//   - 标题区双击进入行内编辑;Enter 保存(走 store applyLocalChange) /
//     ESC 取消 / 空名拒绝 / >30 字符截断 + warning 通知。
//
// 拖拽(从弹层把 App 拖回桌面)由 P5 实装;M4 仅暴露 ``data-folder-id`` 标记
// 与 ``data-app-id`` 给后续脚手架。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { DesktopFolderDTO } from '@blade/os-sdk';

import type { Registry, RegistryEntry } from '../apps/registryContext';
import { useDesktopLayoutStore } from '../store/desktopLayoutStore';
import { pushNotification } from '../store/notificationStore';
import { DesktopAppIcon } from './DesktopAppIcon';
import type { DragPayload } from './desktopDnD';

interface FolderPopoverProps {
  folder: DesktopFolderDTO | null;
  registry: Registry;
  /** 单击 App 时调用;弹层会先关闭再 launch。 */
  onLaunch: (appId: string) => void;
  /** ESC / 点击 App 关闭后焦点应该还给哪个 DOM 节点。 */
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  /**
   * 弹层内 App 拖拽结束 → 转发给 Desktop 的 dispatcher。Desktop 用 payload.source
   * == 'folder' 区分,落到桌面空白 → moveOutOfFolder;其他位置 → 拒绝并通知。
   */
  onMemberDragEnd?: (
    event: DragEvent<HTMLButtonElement>,
    payload: DragPayload,
  ) => void;
}

const FOLDER_NAME_MAX = 30;

export function FolderPopover({
  folder,
  registry,
  onLaunch,
  triggerRef,
  onMemberDragEnd,
}: FolderPopoverProps) {
  const { t } = useTranslation();
  const setOpenFolder = useDesktopLayoutStore((s) => s.setOpenFolder);
  const applyLocalChange = useDesktopLayoutStore((s) => s.applyLocalChange);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const firstAppRef = useRef<HTMLButtonElement | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');

  // 文件夹被解散(已空) → 自动关闭。
  //
  // 原来的条件是 ``length <= 1``,配套 moveOutOfFolder"剩 ≤1 就解散"的规则。
  // 但「右键 App →移入新建文件夹」会显式产出**单成员**文件夹(空文件夹存不住,
  // 见 createFolderWithApp),那种文件夹必须点得开 —— 否则用户建完就打不开、
  // 也没法往里拖第二个。所以这里收窄成"空了才关",拖出解散的规则不动。
  useEffect(() => {
    if (folder !== null && folder.items.length < 1) {
      setOpenFolder(null);
    }
  }, [folder, setOpenFolder]);

  // 打开时聚焦第一个 App。folder 切换时也要重新聚焦。
  useEffect(() => {
    if (!folder) return;
    // 让浏览器先把 popover 渲染出来再聚焦。
    const id = window.setTimeout(() => {
      firstAppRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [folder?.id]);

  const close = useCallback(() => {
    setOpenFolder(null);
    setEditing(false);
    // 焦点回到触发按钮 —— 关闭路径(ESC / 点 backdrop)统一走这里。
    const trigger = triggerRef.current;
    if (trigger) {
      // Some elements (e.g., button removed during re-render) may already be
      // detached;失败静默吞掉。
      try {
        trigger.focus();
      } catch {
        /* ignore */
      }
    }
  }, [setOpenFolder, triggerRef]);

  // ESC 关闭(同时也用于退出编辑态)
  useEffect(() => {
    if (!folder) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      // 编辑中按 ESC → 仅退出编辑态,弹层不关闭。
      if (editing) {
        setEditing(false);
        return;
      }
      close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [folder, editing, close]);

  const members: RegistryEntry[] = useMemo(() => {
    if (!folder) return [];
    return folder.items
      .map((appId) => registry.get(appId))
      .filter((e): e is RegistryEntry => Boolean(e));
  }, [folder, registry]);

  if (!folder) return null;

  function startEdit() {
    setDraftName(folder!.name);
    setEditing(true);
  }

  function commitEdit() {
    const raw = draftName;
    const trimmed = raw.trim();
    if (!trimmed) {
      // 空名拒绝;退出编辑态,name 保持原样。
      setEditing(false);
      return;
    }
    let next = trimmed;
    if (next.length > FOLDER_NAME_MAX) {
      next = next.slice(0, FOLDER_NAME_MAX);
      pushNotification({
        level: 'warning',
        message: t('desktop.folder.nameTooLong'),
        key: 'desktop-folder-name-too-long',
      });
    }
    if (next !== folder!.name) {
      applyLocalChange((draft) => {
        const targetFolder = draft.folders.find((f) => f.id === folder!.id);
        if (targetFolder) targetFolder.name = next;
        for (const it of draft.items) {
          if (it.type === 'folder' && it.folderId === folder!.id) {
            it.name = next;
          }
        }
      });
    }
    setEditing(false);
  }

  function cancelEdit() {
    setEditing(false);
  }

  function onLaunchApp(appId: string) {
    setOpenFolder(null);
    setEditing(false);
    onLaunch(appId);
  }

  return (
    <div
      className="os-folder-popover-backdrop"
      onMouseDown={(e) => {
        // 点击遮罩区 → 关闭。点击 panel 内部不触发(stopPropagation)。
        if (e.target === e.currentTarget) close();
      }}
      // 弹层是模态的,里面右键弹出浏览器的「返回/重新加载」很出戏 —— 统一吃掉。
      // 桌面壳那层的拦截到不了这里:弹层挂在 Desktop 根节点之外的 DOM 位置。
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`folder-title-${folder.id}`}
        tabIndex={-1}
        className="os-folder-popover-panel"
        data-folder-id={folder.id}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {editing ? (
          <input
            className="os-folder-popover-title-input"
            value={draftName}
            autoFocus
            // 限 30 + 1 防误操作把字符吃太多;真正截断在 commit 时执行
            // (用户感知:输入超限会被静默 trim,弹通知)。
            maxLength={FOLDER_NAME_MAX + 16}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitEdit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            onBlur={commitEdit}
            aria-label={t('desktop.folder.newName')}
          />
        ) : (
          <h2
            id={`folder-title-${folder.id}`}
            className="os-folder-popover-title"
            onDoubleClick={startEdit}
            title={folder.name}
          >
            {folder.name}
          </h2>
        )}
        <div role="grid" className="os-folder-popover-grid">
          {members.map((entry, idx) => (
            <div
              className="os-folder-popover-cell"
              key={entry.id}
              ref={idx === 0 ? (el) => {
                firstAppRef.current = el?.querySelector('button') ?? null;
              } : undefined}
            >
              <DesktopAppIcon
                entry={entry}
                fallbackIndex={idx}
                onActivate={() => onLaunchApp(entry.id)}
                dragSource="folder"
                folderId={folder.id}
                onDragEnd={onMemberDragEnd}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
